/**
 * Pure layout math for {@link useOverflowLayout}, split out so it has no React
 * dependency and can be reasoned about / checked in isolation. All three past bugs
 * in this feature lived in the promotion index math, so it lives here on its own.
 */
/**
 * Derive the concrete row layout from a single `budget` (how many item-slots the
 * row shows) plus which item is active. Keeping this a pure function of
 * `budget + activeIndex` is what prevents the active item from ever being stranded
 * in the dropdown: whatever shrinks the budget, the active item is re-promoted here.
 *
 * - `budget === null` or `>= items.length` → everything fits, no "More" button.
 * - `budget <= 0` → only the button; caller shows the active item's label on it.
 * - active index `>= budget` → promote it into the last visible slot.
 * - otherwise → plain slice.
 */
export function deriveOverflowLayout(items, budget, activeId) {
    var _a;
    const n = items.length;
    if (budget === null || budget >= n) {
        return { orderedItems: items, hiddenIds: new Set(), overflowItems: [], promotedId: null, visibleCount: n };
    }
    if (budget <= 0) {
        return { orderedItems: items, hiddenIds: new Set(items.map(i => i.id)), overflowItems: items, promotedId: null, visibleCount: 0 };
    }
    const a = items.findIndex(it => it.id === activeId);
    let visibleRegular;
    let promoted;
    let hidden;
    if (a >= budget) {
        // Active would overflow → give up the last slot to promote it.
        visibleRegular = items.slice(0, budget - 1);
        promoted = items[a];
        hidden = items.filter((_, idx) => idx >= budget - 1 && idx !== a);
    }
    else {
        visibleRegular = items.slice(0, budget);
        promoted = null;
        hidden = items.slice(budget);
    }
    return {
        orderedItems: [...visibleRegular, ...(promoted ? [promoted] : []), ...hidden],
        hiddenIds: new Set(hidden.map(i => i.id)),
        overflowItems: hidden,
        promotedId: (_a = promoted === null || promoted === void 0 ? void 0 : promoted.id) !== null && _a !== void 0 ? _a : null,
        visibleCount: visibleRegular.length + (promoted ? 1 : 0),
    };
}
/**
 * Given the natural fit count `f` (how many leading items fit beside the button),
 * the budget to use. `f <= 1` collapses everything into "More" so the row never
 * shows just one lone item beside the button.
 */
export function budgetFromFit(f) {
    return f <= 1 ? 0 : f;
}
